import {FC} from "react"
import {ReactSVG} from "react-svg"

import checkIcon from "@frontend/design/icons/check.svg"
import {useTranslation} from "@frontend/i18n"
import {CauseOnMessageWithCauseAndPart} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

import {PartCard} from "./PartCard"

interface Props {
  cause: CauseOnMessageWithCauseAndPart
  variant: "likely" | "confirmed"
  caseId: string
  modelNumber: string | null
  compact?: boolean
}

export const InlineDiagnosisCauseCard: FC<Props> = ({
  cause,
  variant,
  caseId,
  modelNumber,
  compact = false,
}) => {
  const t = useTranslation()

  const titleKey =
    variant === "confirmed"
      ? "conversation.inlineDiagnosisCause.confirmedTitle"
      : "conversation.inlineDiagnosisCause.likelyTitle"

  const iconBackgroundColor = variant === "confirmed" ? "#0A8035" : "#b0a617"

  return (
    <div
      className={vstack({
        width: "100%",
        maxWidth: 540,
        gap: compact ? 12 : 16,
        alignItems: "stretch",
        borderRadius: 16,
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: "border.causeCard",
        backgroundColor: "background.brand.primarySubtle",
        padding: compact ? 12 : 16,
      })}
    >
      <div
        className={hstack({
          gap: compact ? 10 : 12,
          alignItems: "flex-start",
        })}
      >
        <div
          className={css({
            flexShrink: 0,
            width: compact ? 28 : 32,
            height: compact ? 28 : 32,
            borderRadius: "50%",
            backgroundColor: iconBackgroundColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <ReactSVG
            src={checkIcon}
            className={css({
              "& svg": {
                width: compact ? 16 : 18,
                height: compact ? 16 : 18,
              },
              "& path": {
                fill: "white",
              },
            })}
          />
        </div>
        <div
          className={vstack({
            gap: 4,
            alignItems: "flex-start",
            flex: 1,
            minWidth: 0,
          })}
        >
          <h3
            className={css({
              color: "text.primary",
              textStyle: "bodyMedium",
              fontWeight: 600,
              margin: 0,
            })}
          >
            {t(titleKey, {causeName: cause.cause.name})}
          </h3>
        </div>
      </div>
      <PartCard
        cause={cause}
        caseId={caseId}
        modelNumber={modelNumber}
        compact={compact}
      />
    </div>
  )
}
